import React, { useEffect, useRef, useState } from "react";
import { httpsCallable } from "firebase/functions";

import { functions } from "../../firebase";
import { buildBrochureEmailHtml } from "./brochureEmailTemplate";
import "./BrochureRequestModal.css";

export default function BrochureRequestModal({
  open,
  onClose,
  brochures,
  productName,
}) {
  const [email, setEmail] = useState("");
  const [selectedBrochureFile, setSelectedBrochureFile] = useState(
    brochures[0].file,
  );
  const [status, setStatus] = useState("idle");
  const [message, setMessage] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    inputRef.current?.focus();

    const handleKeyDown = (event) => {
      if (event.key === "Escape" && status !== "sending") onClose();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose, open, status]);

  if (!open) return null;

  const handleSubmit = async (event) => {
    event.preventDefault();
    setStatus("sending");
    setMessage("");

    try {
      const normalizedEmail = email.trim().toLowerCase();
      const selectedBrochure = brochures.find(
        ({ file }) => file === selectedBrochureFile,
      );

      if (!selectedBrochure) {
        throw new Error("Please select a valid brochure.");
      }

      const sendEmail = httpsCallable(functions, "sendEmail");

      await sendEmail({
        to: normalizedEmail,
        from: "AndUs Brochure <noreply@mail.andus.co.za>",
        subject: `Your ${selectedBrochure.title} brochure`,
        isHTML: true,
        html: buildBrochureEmailHtml({ brochureTitle: selectedBrochure.title }),
        ...(selectedBrochure.file && {
          attachmentStoragePath: `brochures/${selectedBrochure.file}`,
          attachmentFilename: selectedBrochure.file,
        }),
      });

      try {
        const firestoreOperations = httpsCallable(
          functions,
          "firestoreOperations",
        );
        await firestoreOperations({
          operation: "write",
          company: "TPC",
          collectionBrand: "TechPerformanceCentre",
          systemName: "programmeBrochureRequests",
          uniqueId: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
          documentData: {
            Program: selectedBrochure.title,
            BrochureFile: selectedBrochure.file,
            Email: normalizedEmail,
            Timestamp: new Date().toISOString(),
            Source: `${productName} product page`,
          },
        });
      } catch (loggingError) {
        console.warn("[Brochure] Email sent, but lead logging failed:", loggingError);
      }

      setStatus("success");
      setMessage(`We sent the brochure to ${normalizedEmail}.`);
    } catch (error) {
      console.error("[Brochure] Could not send brochure:", error);
      setStatus("error");
      setMessage(
        "We couldn't send the brochure right now. Please try again.",
      );
    }
  };

  return (
    <div
      className="brochure-modal__backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && status !== "sending") {
          onClose();
        }
      }}
    >
      <section
        className="brochure-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="brochure-modal-title"
      >
        <button
          type="button"
          className="brochure-modal__close"
          onClick={onClose}
          disabled={status === "sending"}
          aria-label="Close brochure request"
        >
          ×
        </button>

        <p className="brochure-modal__eyebrow">Explore intelligent systems</p>
        <h2 id="brochure-modal-title">Get the {productName} brochure</h2>
        <p className="brochure-modal__intro">
          Enter your email and we’ll send the brochure directly to your inbox.
        </p>

        {status === "success" ? (
          <div className="brochure-modal__success" role="status">
            <span aria-hidden="true">✓</span>
            <strong>Check your inbox</strong>
            <p>{message}</p>
            <button type="button" onClick={onClose}>Done</button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            {brochures.length > 1 && (
              <>
                <label htmlFor="brochure-request-selection">Choose a brochure</label>
                <select
                  id="brochure-request-selection"
                  value={selectedBrochureFile}
                  onChange={(event) => setSelectedBrochureFile(event.target.value)}
                  disabled={status === "sending"}
                  required
                >
                  {brochures.map(({ file, title }) => (
                    <option key={file} value={file}>{title}</option>
                  ))}
                </select>
              </>
            )}

            <label htmlFor="brochure-request-email">Work email</label>
            <input
              ref={inputRef}
              id="brochure-request-email"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="you@company.com"
              autoComplete="email"
              disabled={status === "sending"}
              required
            />
            <button
              className="brochure-modal__submit"
              type="submit"
              disabled={status === "sending"}
            >
              {status === "sending" ? "Sending brochure…" : "Email me the brochure"}
            </button>
            {status === "error" && (
              <p className="brochure-modal__error" role="alert">{message}</p>
            )}
            <p className="brochure-modal__privacy">
              We’ll only use your email to send the brochure and relevant product information.
            </p>
          </form>
        )}
      </section>
    </div>
  );
}
